import React, { useState, useContext, useEffect } from "react";

import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  TableBody,
  Typography,
  Box,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import UploadDocumentModal from "../AddKnowledgebase/UploadDocument";
import ImportWebsiteModal from "../AddKnowledgebase/ImportWebsite";
import ConfirmDeleteModal from "./ConfirmDeleteModal";

import { appLogger, Http } from "../../../utils";
import { UserContext } from "../../../context/userContext";
import { UserLoginInfo } from "../../../types/context";

import { withSnackbar } from "../../../utils/common/SnackbarHoc";

import uuid from "react-uuid";

import { useParams } from "react-router-dom";

import CollapsableRow from "./CollapsableRow";

function _TablePaginationActions(props: TablePaginationActionsProps) {
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        <FirstPageIcon />
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        <KeyboardArrowRight />
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        <LastPageIcon />
      </IconButton>
    </Box>
  );
}

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number
  ) => void;
}

const tableHeaders = ["", "Name", "Status", "Edited On", "Created On"];

function compareName(a: any, b: any, order: any) {
  if (order === "asc") {
    return a?.name?.localeCompare(b?.name);
  }
  if (order === "desc") {
    return b?.name?.localeCompare(a?.name);
  }
}

function compareStatus(a: any, b: any, order: any) {
  if (order === "asc") {
    return a?.status?.localeCompare(b?.name);
  }
  if (order === "desc") {
    return b?.status?.localeCompare(a?.name);
  }
}

function compareCreatedOn(a: any, b: any, order: any) {
  if (order === "asc") {
    return new Date(a?.createdOn).getTime() - new Date(b?.createdOn).getTime();
  }
  if (order === "desc") {
    return new Date(b?.createdOn).getTime() - new Date(a?.createdOn).getTime();
  }
}

function compareEditedOn(a: any, b: any, order: any) {
  if (order === "asc") {
    return (
      new Date(a?.lastModifiedOn).getTime() -
      new Date(b?.lastModifiedOn).getTime()
    );
  }
  if (order === "desc") {
    return (
      new Date(b?.lastModifiedOn).getTime() -
      new Date(a?.lastModifiedOn).getTime()
    );
  }
}

const DocumentsTable = ({
  storedDocuments = [],
  handleRetrainDocuments,
  handleFetchStoredDocuments,
  setFileUploading,
  snackbarShowMessage,
  setStoredDocuments,
  isChatGPTAPIKeyValid,
}: {
  storedDocuments: any;
  handleFetchStoredDocuments: () => void;
  handleRetrainDocuments: (id: string) => void;
  handleDeleteDocument: (id: string) => void;
  setFileUploading: (value: any) => void;
  snackbarShowMessage: any;
  setStoredDocuments: any;
  isChatGPTAPIKeyValid?: boolean;
}) => {
  const { productId, versionId } = useParams();

  const { userInfo } = useContext(UserContext) as UserLoginInfo;
  const [selectedFolder, setSelectedFolder] = useState<any | null>(null);

  const [uploadDocumentModal, setUploadDocumentModal] =
    useState<boolean>(false);

  const [importWebsiteModal, setImportWebsiteModal] = useState<boolean>(false);

  const [confirmDeleteModal, setConfirmDeleteModal] = useState<boolean>(false);

  const [_page, setPage] = useState(0);
  const [_rowsPerPage, setRowsPerPage] = useState(10);
  const [_itemLimit, setItemLimit] = useState({
    lowerLimit: 0,
    upperLimit: 9,
  });

  const [orderBy, setOrderBy] = useState("Name");
  const [order, setOrder] = useState<"asc" | "desc">("desc");

  const handleOpenConfirmDeleteModal = (folder: any) => {
    setSelectedFolder(folder);
    setConfirmDeleteModal(true);
  };

  const handleCloseConfirmDeleteModal = () => {
    setSelectedFolder(null);
    setConfirmDeleteModal(false);
  };

  const handleOpenImportWebsiteModal = (folder: any) => {
    setSelectedFolder(folder);
    setImportWebsiteModal(true);
  };

  const handleCloseImportWebsiteModal = () => {
    setSelectedFolder(null);
    setImportWebsiteModal(false);
  };

  const handleOpenUploadDocumentModal = (folder: any) => {
    setSelectedFolder(folder);
    setUploadDocumentModal(true);
  };

  const handleCloseUploadDocumentModal = () => {
    setSelectedFolder(null);
    setUploadDocumentModal(false);
  };

  const _handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const _handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    if (event.target.value === "-1") {
      setItemLimit({
        lowerLimit: 0,
        upperLimit: storedDocuments.length - 1,
      });
    } else {
      setItemLimit({
        lowerLimit: 0,
        upperLimit: parseInt(event.target.value, 10) - 1,
      });
    }
  };

  const handleRequestSort = (property: string) => {
    if (orderBy === property) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setOrder("asc");
      setOrderBy(property);
    }
  };

  const handleSortKnowledgebase = (sortFunction: any) => {
    const tempDocuments = [...storedDocuments];
    const sorted = tempDocuments.map((documents: any) => {
      const newDocuments = {
        ...documents,
        fields: documents.fields.sort((a: any, b: any) =>
          sortFunction(a, b, order)
        ),
      };

      return newDocuments;
    });

    return sorted;
  };

  const handleSortKnowledgebaseByName = (sortFunction: any) => {
    const tempDocuments = [...storedDocuments];
    const sortedFolders = tempDocuments.sort((a: any, b: any) =>
      sortFunction(a, b, order)
    );

    const sorted = sortedFolders.map((documents: any) => {
      const newDocuments = {
        ...documents,
        fields: documents.fields.sort((a: any, b: any) =>
          sortFunction(a, b, order)
        ),
      };

      return newDocuments;
    });

    return sorted;
  };

  useEffect(() => {
    if (storedDocuments.length > 0) {
      if (orderBy === "Name") {
        const sorted = handleSortKnowledgebaseByName(compareName);
        setStoredDocuments(sorted);
      }

      if (orderBy === "Status") {
        const sorted = handleSortKnowledgebase(compareStatus);
        setStoredDocuments(sorted);
      }

      if (orderBy === "Edited On") {
        const sorted = handleSortKnowledgebase(compareEditedOn);
        setStoredDocuments(sorted);
      }

      if (orderBy === "Created On") {
        const sorted = handleSortKnowledgebase(compareCreatedOn);
        setStoredDocuments(sorted);
      }
    }
  }, [order, orderBy]);

  const handleStatusColor = (color: string) => {
    if (color === "error") {
      return "#d32f2f";
    }

    if (color === "Processing") {
      return "#e1ad01";
    }

    if (color === "learning" || color === "Learning") {
      return "#e1ad01";
    }

    if (color === "live") {
      return "#2e7d32";
    }
  };

  const handleGetS3Url = async (
    values: any,
    isRetrain: boolean,
    document: any
  ) => {
    try {
      const fileName = `${uuid()}.${values?.name?.split(".").pop()}`;

      const selected = isRetrain ? document : selectedFolder;

      const res: any = await Http.post({
        url: `/api/v2/kbArticles/docs`,
        state: userInfo,
        body: {
          productId: productId,
          productVersion: versionId,
          folderName: isRetrain ? selected.folder : selected.id,
          fileName,
          fileType: values.type,
          userFileName: values.name,
          oldId: isRetrain ? selected.id : "",
        },
      });

      if (res?.status === "File already exists") {
        setFileUploading({ uploading: false, name: "", percentage: 0 });
        snackbarShowMessage("File already exists", "error", 3000);
        return;
      }

      if (res?.signedURL) {
        return { data: res.signedURL, fileName };
      }
    } catch (err) {
      appLogger.error({
        infoObj: {
          method: "UploadDocument",
          component: "Knowledgebase",
          fileName: "UploadDocument.tsx",
        },
      });
      setFileUploading({ uploading: false, name: "", percentage: 0 });
    }
  };

  return (
    <Box className="products-table table-wrap">
      <Table sx={{ mb: 3 }}>
        <TableHead>
          <TableRow>
            {tableHeaders.map((header) => {
              return (
                <TableCell key={header}>
                  {header && (
                    <TableSortLabel
                      active={orderBy === header}
                      direction={orderBy === header ? order : "asc"}
                      onClick={() => {
                        handleRequestSort(header);
                      }}
                      hideSortIcon={false}
                    >
                      <Typography
                        sx={{
                          color: "rgba(32, 33, 36, 0.5)!important",
                          fontSize: "12px",
                        }}
                      >
                        {header}
                      </Typography>
                      <span
                        className={
                          orderBy === header
                            ? `custom-sort-icon active ${order}`
                            : "custom-sort-icon"
                        }
                      >
                        <ArrowUpwardIcon
                          sx={{
                            color: "rgba(32, 33, 36, 0.5)",
                          }}
                        />
                        <ArrowDownwardIcon
                          sx={{
                            color: "rgba(32, 33, 36, 0.5)",
                          }}
                        />
                      </span>
                    </TableSortLabel>
                  )}
                </TableCell>
              );
            })}

            <TableCell>
              <Typography
                sx={{
                  color: "rgba(32, 33, 36, 0.5)!important",
                  fontSize: "12px",
                }}
              >
                Actions
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {storedDocuments.map((document: any) => {
            return (
              <CollapsableRow
                document={document}
                handleStatusColor={handleStatusColor}
                handleOpenUploadDocumentModal={handleOpenUploadDocumentModal}
                handleRetrainDocuments={handleRetrainDocuments}
                handleOpenConfirmDeleteModal={handleOpenConfirmDeleteModal}
                handleOpenImportWebsiteModal={handleOpenImportWebsiteModal}
                handleFetchStoredDocuments={handleFetchStoredDocuments}
                setFileUploading={setFileUploading}
                handleGetS3Url={handleGetS3Url}
                isChatGPTAPIKeyValid={isChatGPTAPIKeyValid}
              />
            );
          })}
        </TableBody>
      </Table>

      {/* <TablePagination
        component="div"
        rowsPerPageOptions={[10, 15, 20]}
        count={10}
        rowsPerPage={rowsPerPage}
        page={page}
        SelectProps={{
          inputProps: {
            "aria-label": "rows per page",
          },
          native: true,
        }}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ActionsComponent={TablePaginationActions}
      /> */}

      {uploadDocumentModal && (
        <UploadDocumentModal
          open={uploadDocumentModal}
          onClose={handleCloseUploadDocumentModal}
          handleFetchStoredDocuments={handleFetchStoredDocuments}
          setFileUploading={setFileUploading}
          selectedFolder={selectedFolder}
          handleGetS3Url={handleGetS3Url}
          isChatGPTAPIKeyValid={isChatGPTAPIKeyValid}
        />
      )}

      {importWebsiteModal && (
        <ImportWebsiteModal
          open={importWebsiteModal}
          onClose={handleCloseImportWebsiteModal}
          handleFetchStoredDocuments={handleFetchStoredDocuments}
          selectedFolder={selectedFolder}
          isChatGPTAPIKeyValid={isChatGPTAPIKeyValid}
        />
      )}

      {confirmDeleteModal && (
        <ConfirmDeleteModal
          open={confirmDeleteModal}
          onClose={handleCloseConfirmDeleteModal}
          handleFetchStoredDocuments={handleFetchStoredDocuments}
          selectedFolder={selectedFolder}
        />
      )}
    </Box>
  );
};

export default withSnackbar(DocumentsTable);
